import { useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import {
  MagnifyingGlassPlusIcon,
  MagnifyingGlassMinusIcon,
  CaretLeftIcon,
  CaretRightIcon,
} from '@phosphor-icons/react';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import * as Sentry from '@sentry/react';

// Configure PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url,
).toString();

interface ModernPDFViewerProps {
  url: string;
  width?: number;
}

export function ModernPDFViewer({ url, width = 500 }: ModernPDFViewerProps) {
  const [numPages, setNumPages] = useState<number | null>(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [scale, setScale] = useState(1);

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
  };

  const zoomIn = () => setScale((prev) => Math.min(prev + 0.1, 2));
  const zoomOut = () => setScale((prev) => Math.max(prev - 0.1, 0.5));

  return (
    <Card className="h-full flex flex-col">
      {/* Controls Header */}
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          {/* Page Navigation */}
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPageNumber(Math.max(1, pageNumber - 1))}
              disabled={pageNumber <= 1}
            >
              <CaretLeftIcon className="h-4 w-4" />
            </Button>
            <span className="text-sm text-muted-foreground">
              Page {pageNumber} of {numPages || '?'}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                setPageNumber(Math.min(numPages || 1, pageNumber + 1))
              }
              disabled={!numPages || pageNumber >= numPages}
            >
              <CaretRightIcon className="h-4 w-4" />
            </Button>
          </div>

          {/* Zoom Controls */}
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={zoomOut}
              disabled={scale <= 0.5}
            >
              <MagnifyingGlassMinusIcon className="h-4 w-4" />
            </Button>
            <span className="text-sm text-muted-foreground min-w-[3rem] text-center">
              {Math.round(scale * 100)}%
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={zoomIn}
              disabled={scale >= 2}
            >
              <MagnifyingGlassPlusIcon className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardHeader>

      {/* PDF Content */}
      <CardContent className="flex-1 overflow-auto p-4">
        <Document
          file={url}
          onLoadSuccess={onDocumentLoadSuccess}
          loading={
            <div className="flex justify-center items-center min-h-[400px]">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          }
          onLoadError={(err) => {
            Sentry.captureException(err);
            console.error(err);
          }}
          error={
            <div className="text-center py-8">
              <p className="text-destructive">
                Error loading PDF. Please try downloading instead.
              </p>
            </div>
          }
        >
          <Page
            pageNumber={pageNumber}
            renderTextLayer={false}
            renderAnnotationLayer={false}
            width={width * scale}
            loading={
              <div className="flex justify-center items-center min-h-[400px]">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
              </div>
            }
          />
        </Document>
      </CardContent>
    </Card>
  );
}
